'use client';
import { zodResolver } from '@hookform/resolvers/zod';
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FiEye, FiEyeOff, FiInfo } from 'react-icons/fi';
import { z } from 'zod';

import { type ProviderDetail } from '@/api';
import { Button } from '@/components/button/button';
import { Menu, MenuItem } from '@/components/dropdownMenu/dropdownMenu';
import { Icon } from '@/components/icon/icon';
import { Input } from '@/components/input/input';
import { Loader } from '@/components/loader/loader';
import Modal from '@/components/modal/modal';
import { Tooltip } from '@/components/tooltip/tooltip';
import { Heading } from '@/components/typography/heading';
import { Text } from '@/components/typography/text';
import {
  useCreateModel, useModelInfoQuery, useModelsQuery, useUpdateModel
} from '@/hooks/queries/useModelsQuery';
import envVarsProvider from '@/utils/envVars/envVars';

import styles from './modelModal.module.scss';

// Zod schema for form validation
const createFormSchema = () => z.object( {
  provider: z.string().min( 1, 'Provider is required' ),
  providerModel: z.string()
    .min( 1, 'Provider model is required' )
    .refine(
      ( model ) => !model.includes( '*' ) && !model.includes( '?' ) && !model.includes( '[' ) && !model.includes( ']' ),
      'Wildcards are not allowed in the provider model name'
    ),
  modelName: z.string().min( 1, 'Model name is required' ),
  description: z.string().optional(),
  credentialValues: z.record( z.string(), z.string() )
} );

type FormData = z.infer<ReturnType<typeof createFormSchema>>;

interface ModelModalProps {
  mode: 'add' | 'edit';
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  modelId?: string; // Required for edit mode
  providers: ProviderDetail[];
  isLoadingProviders: boolean;
}

export function ModelModal( { mode, isOpen, onClose, onSuccess, modelId, providers, isLoadingProviders }: ModelModalProps ) {
  const [ isSubmitting, setIsSubmitting ] = useState( false );
  const [ error, setError ] = useState( '' );
  const [ selectedProvider, setSelectedProvider ] = useState<ProviderDetail | null>( null );
  const [ passwordVisibility, setPasswordVisibility ] = useState<Record<string, boolean>>( {} );
  const [ initialFormData, setInitialFormData ] = useState<FormData | null>( null );

  // Fetch existing models for uniqueness validation
  const modelsQueryOptions = useMemo( () => ( { enabled: isOpen } ), [ isOpen ] );
  const { data: existingModels } = useModelsQuery( modelsQueryOptions );

  // Fetch model info when in edit mode
  const modelInfoQueryOptions = useMemo(
    () => ( { enabled: mode === 'edit' && isOpen && !!modelId } ),
    [ mode, isOpen, modelId ]
  );
  const {
    data: modelInfo,
    isLoading,
    error: loadError
  } = useModelInfoQuery( modelId, modelInfoQueryOptions );

  // Mutations for create and update
  const createModelMutation = useCreateModel();
  const updateModelMutation = useUpdateModel();

  // React Hook Form setup
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
    formState: { errors }
  } = useForm<FormData>( {
    resolver: zodResolver( createFormSchema() ),
    defaultValues: {
      provider: '',
      providerModel: '',
      modelName: '',
      description: '',
      credentialValues: {}
    }
  } );

  // Watch form values
  const provider = watch( 'provider' );
  const providerModel = watch( 'providerModel' );
  const modelName = watch( 'modelName' );
  const credentialValues = watch( 'credentialValues' );

  // Process model data when it's loaded
  useEffect( () => {
    if ( mode === 'edit' && modelInfo && providers ) {
      setError( '' );

      if ( modelInfo.data && modelInfo.data.length > 0 ) {
        const modelData = modelInfo.data[0];

        // Extract the provider from the litellm_params.model, falling back to custom_llm_provider
        // for models stored without a provider prefix (e.g. OpenAI's "gpt-4o" vs "openai/gpt-4o")
        const modelStr = modelData.litellm_params.model;
        const providerFromModel = modelStr.includes( '/' )
          ? modelStr.split( '/' )[0]
          : modelData.litellm_params.custom_llm_provider;

        // Find matching provider
        const matchingProvider = providers?.find(
          p => providerFromModel && p.litellm_provider.toLowerCase() === providerFromModel.toLowerCase()
        );

        if ( matchingProvider ) {
          setSelectedProvider( matchingProvider );

          // Extract credential values from litellm_params
          const credentials: Record<string, string> = {};
          matchingProvider.credential_fields?.forEach( field => {
            const value = modelData.litellm_params[field.key];
            if ( typeof value === 'string' && value ) {
              credentials[field.key] = value;
            }
          } );

          const modelOnly = modelStr.includes( '/' ) ? modelStr.split( '/' ).slice( 1 ).join( '/' ) : modelStr;

          const formData: FormData = {
            provider: matchingProvider.litellm_provider,
            providerModel: modelOnly,
            modelName: modelData.model_name,
            description: modelData.model_info?.description || '',
            credentialValues: credentials
          };

          // Set form values
          Object.entries( formData ).forEach( ( [ key, value ] ) => {
            setValue( key as keyof FormData, value );
          } );

          // Store initial form data for change detection
          setInitialFormData( formData );
        } else {
          setError( 'Unable to find matching provider for this model' );
        }
      } else {
        setError( 'Model not found' );
      }
    }
  }, [ modelInfo, mode, providers, setValue ] );

  // Handle loading errors
  useEffect( () => {
    if ( loadError ) {
      const errorMessage = loadError instanceof Error ? loadError.message : 'Failed to load model data';
      setError( errorMessage );
    }
  }, [ loadError ] );

  // Auto-fill Model Name with Provider Model value for KanpAI providers
  useEffect( () => {
    if ( isKanpAIProvider() && providerModel && ( mode === 'add' || !initialFormData ) ) {
      setValue( 'modelName', providerModel );
    }
  }, [ providerModel, selectedProvider, setValue, mode, initialFormData ] );

  // Check if model name is unique (excluding current model in edit mode)
  const isModelNameUnique = ( name: string ): boolean => {
    if ( !name || !existingModels ) return true;
    // In edit mode, allow the current model name
    if ( mode === 'edit' && name.toLowerCase() === initialFormData?.modelName.toLowerCase() ) return true;
    return !existingModels.some( m => ( m.publicName && m.publicName.toLowerCase() === name.toLowerCase() ) ||
      ( m.name && m.name.toLowerCase() === name.toLowerCase() ) );
  };

  const handleCredentialChange = ( key: string, value: string ) => {
    setValue( 'credentialValues', { ...credentialValues, [key]: value } );
  };

  const togglePasswordVisibility = ( key: string ) => {
    setPasswordVisibility( prev => ( { ...prev, [key]: !prev[key] } ) );
  };

  // Helper function to detect if current provider is KanpAI
  const isKanpAIProvider = (): boolean => {
    return selectedProvider?.provider?.toLowerCase().includes( 'kanpai' ) ||
           selectedProvider?.litellm_provider?.toLowerCase().includes( 'kanpai' ) ||
           false;
  };

  // Get dynamic tooltip based on provider type
  const getModelNameTooltip = (): string => {
    if ( isKanpAIProvider() ) {
      return 'The Model Name is specified in API calls within this platform. It is shown to users when selecting or configuring models. Example: The Model Name kanpai-model is passed to the API with model = "kanpai-model" Result: kanpai-model is sent to KanpAI™';
    }

    return 'The Model Name is specified in API calls within this platform. It is shown to users when selecting or configuring models. Example: You make the Model Name my-kanpai-name and make the Provider Model claude-3-5-haiku-20241022 Usage: You make an API call with model = "my-kanpai-name" Result: claude-3-5-haiku-20241022 is sent to the provider';
  };

  const providerModelPlaceholder = 'Enter the exact model identifier';

  // Check if form has changed from initial values (only for edit mode)
  const hasFormChanged = (): boolean => {
    if ( mode === 'add' || !initialFormData ) return true;

    const currentData = {
      provider,
      providerModel,
      modelName,
      description: watch( 'description' ),
      credentialValues
    };

    return (
      currentData.provider !== initialFormData.provider ||
      currentData.providerModel !== initialFormData.providerModel ||
      currentData.modelName !== initialFormData.modelName ||
      currentData.description !== initialFormData.description ||
      JSON.stringify( currentData.credentialValues ) !== JSON.stringify( initialFormData.credentialValues )
    );
  };

  const onSubmit = async( data: FormData ) => {
    setError( '' );
    setIsSubmitting( true );

    try {
      // Build the request payload
      const litellmParams: {
        model: string;
        [key: string]: unknown;
      } = {
        model: `${ data.provider }/${ data.providerModel }`,
        custom_llm_provider: selectedProvider?.litellm_provider
      };

      // Add all credential fields from the provider
      if ( selectedProvider?.credential_fields ) {
        selectedProvider.credential_fields.forEach( field => {
          const value = data.credentialValues[field.key];
          if ( value ) {
            litellmParams[field.key] = value;
          }
        } );
      }

      const params = {
        model_name: data.modelName,
        litellm_params: litellmParams,
        model_info: {
          ...( mode === 'edit' && { id: modelId! } ),
          mode: 'chat',
          description: data.description
        }
      };

      if ( mode === 'edit' ) {
        await updateModelMutation.mutateAsync( params );
      } else {
        await createModelMutation.mutateAsync( params );
      }

      onSuccess();
      handleCancel();
    } catch( err ) {
      const errorMessage = err instanceof Error ? err.message : `Failed to ${ mode } model`;
      setError( errorMessage );
    } finally {
      setIsSubmitting( false );
    }
  };

  const handleCancel = () => {
    reset();
    setPasswordVisibility( {} );
    setError( '' );
    setSelectedProvider( null );
    setInitialFormData( null );
    onClose();
  };

  // Check if all required fields are filled
  const areRequiredCredentialsFilled = (): boolean => {
    if ( !selectedProvider?.credential_fields ) return true;

    return selectedProvider.credential_fields.every( field => {
      if ( !field.required ) return true;
      return !!credentialValues[field.key];
    } );
  };

  // Validate model name uniqueness and format (conditional based on KanpAI)
  const getModelNameError = (): string | undefined => {
    if ( !modelName ) return undefined;
    if ( isKanpAIProvider() ) return undefined; // Skip validation for KanpAI

    const validPattern = /^[a-z0-9._\-/]+$/;
    if ( !validPattern.test( modelName ) ) {
      return 'Only lowercase letters, numbers, dashes, underscores, forward slashes, and periods are allowed';
    }

    if ( !isModelNameUnique( modelName ) ) {
      return 'This model name is already in use. Please choose a unique name.';
    }

    return undefined;
  };

  const modelNameError = getModelNameError();
  const providerModelError = errors.providerModel?.message;

  const isFormValid = provider && providerModel && modelName &&
                      !modelNameError && !providerModelError &&
                      areRequiredCredentialsFilled() &&
                      hasFormChanged();

  if ( !isOpen ) return null;

  return (
    <Modal
      modalType={ mode === 'edit' ? 'edit-model' : 'add-model' }
      className={ clsx( styles.modal ) }
      minSize={ { width: 550, height: 500 } }
      maxSize={ { width: 550, height: 800 } }
    >
      <div className={ clsx( styles.modal_content ) }>
        {/* Sticky Header */}
        <div className={ clsx( styles.modal_header ) }>
          <Heading as="h2" size="lg" className={ clsx( styles.title ) }>
            {mode === 'edit' ? 'Edit Model' : 'Add a Model'}
          </Heading>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className={ clsx( styles.loading_container ) }>
            <Loader />
          </div>
        )}

        {/* Scrollable Content */}
        {!isLoading && (
          <form onSubmit={ handleSubmit( onSubmit ) } className={ clsx( styles.form ) }>
            <div className={ clsx( styles.scrollable_content ) }>
              {/* Name of Model Provider */}
              <div className={ clsx( styles.form_field ) }>
                <Menu
                  label="Name of Model Provider"
                  value={ selectedProvider?.provider_display_name || ( isLoadingProviders ? 'Loading providers...' : 'Select a model provider' ) }
                  variant="select"
                  searchable
                  disabled={ mode === 'edit' && isKanpAIProvider() }
                >
                  {providers?.map( ( p ) => (
                    <MenuItem
                      key={ p.provider }
                      value={ p.provider_display_name }
                      onClick={ () => {
                        setValue( 'provider', p.litellm_provider );
                        setSelectedProvider( p );
                        setValue( 'providerModel', '' );
                        if ( mode !== 'edit' ) {
                          setValue( 'modelName', '' );
                        }
                        setValue( 'credentialValues', {} );
                        setPasswordVisibility( {} );
                      } }
                    />
                  ) )}
                </Menu>
                <Text as="span" size="sm" className={ clsx( mode === 'edit' && isKanpAIProvider() ? styles.helper_text : styles.contact_link ) }>
                  {mode === 'edit' && isKanpAIProvider() ? (
                    'Provider cannot be changed for KanpAI™ models'
                  ) : (
                    <>
                      Don&apos;t see your provider?{' '}
                      <a
                        href={ `mailto:${ envVarsProvider.get( 'CONTACT_US_EMAIL' ) }` }
                        className={ clsx( styles.contact_button ) }
                      >
                        Contact us
                      </a>
                    </>
                  )}
                </Text>
              </div>

              {/* Provider Model (Text Field) */}
              {provider && (
                <div className={ clsx( styles.form_field ) }>
                  <Text as="label" size="sm" weight="normal" className={ clsx( styles.label ) }>
                    Provider Model
                    <Tooltip content="The model identifier sent to the provider's API. The selected provider is prepended automatically.">
                      <Icon
                        icon={ FiInfo }
                        style={ { fontSize: '12px', marginLeft: '4px', cursor: 'help' } }
                      />
                    </Tooltip>
                  </Text>
                  <Input
                    type="text"
                    placeholder={ providerModelPlaceholder }
                    { ...register( 'providerModel' ) }
                  />
                  {providerModelError && (
                    <Text as="span" size="sm" className={ clsx( styles.validation_error ) }>
                      {providerModelError}
                    </Text>
                  )}
                </div>
              )}

              {/* KanpAI API Details Section */}
              <div className={ clsx( styles.kanpai_section ) }>
                <Heading as="h3" size="md" className={ clsx( styles.section_title ) }>
                  KanpAI™ API Details
                </Heading>

                {providerModel ? (
                  <>
                    {/* Model Name */}
                    <div className={ clsx( styles.form_field ) }>
                      <Text as="label" size="sm" weight="normal" className={ clsx( styles.label ) }>
                        Model Name
                        <Tooltip content={ getModelNameTooltip() }>
                          <Icon
                            icon={ FiInfo }
                            style={ { fontSize: '12px', marginLeft: '4px', cursor: 'help' } }
                          />
                        </Tooltip>
                      </Text>
                      <Input
                        type="text"
                        placeholder="Persistent name shown to end users"
                        { ...register( 'modelName' ) }
                        disabled={ isKanpAIProvider() || mode === 'edit' }
                      />
                      {modelNameError && (
                        <Text as="span" size="sm" className={ clsx( styles.validation_error ) }>
                          {modelNameError}
                        </Text>
                      )}
                      {!isKanpAIProvider() && mode !== 'edit' && (
                        <Text as="span" size="sm" className={ clsx( styles.helper_text ) }>
                          Use only lowercase letters, numbers, dashes, underscores, forward slashes, and periods
                        </Text>
                      )}
                    </div>

                    {/* Model Description */}
                    <div className={ clsx( styles.form_field ) }>
                      <Input
                        label="Model Description (OPTIONAL)"
                        type="text"
                        placeholder="An optional internal description of this model"
                        { ...register( 'description' ) }
                      />
                    </div>
                  </>
                ) : (
                  <Text as="p" size="sm" className={ clsx( styles.credentials_placeholder ) }>
                    Detail fields will appear here once a Provider Model is selected.
                  </Text>
                )}
              </div>

              {/* Credential Fields Section */}
              <div className={ clsx( styles.credentials_section ) }>
                <Heading as="h3" size="md" className={ clsx( styles.section_title ) }>
                  Enter Provider Credentials
                </Heading>

                {providerModel ? (
                  selectedProvider?.credential_fields && selectedProvider.credential_fields.length > 0 ? (
                    <div className={ clsx( styles.credentials_fields ) }>
                      {selectedProvider.credential_fields.map( ( field ) => {
                        const isPassword = field.field_type === 'password';
                        const isPasswordVisible = passwordVisibility[field.key];

                        return (
                          <div key={ field.key } className={ clsx( styles.form_field ) }>
                            <Text as="label" size="sm" weight="normal" className={ clsx( styles.label ) }>
                              {field.label}{field.required ? '' : ' (OPTIONAL)'}
                              {field.tooltip && (
                                <Tooltip content={ field.tooltip }>
                                  <Icon
                                    icon={ FiInfo }
                                    style={ { fontSize: '12px', marginLeft: '4px', cursor: 'help' } }
                                  />
                                </Tooltip>
                              )}
                            </Text>
                            <Controller
                              name="credentialValues"
                              control={ control }
                              render={ ( { field: controllerField } ) => (
                                <Input
                                  type={ isPassword && !isPasswordVisible ? 'password' : 'text' }
                                  placeholder={ field.placeholder || 'Type...' }
                                  value={ ( controllerField.value as Record<string, string> )[field.key] || field.default_value || '' }
                                  onChange={ ( e ) => handleCredentialChange( field.key, e.target.value ) }
                                  trailingAction={ isPassword ? (
                                    <Icon
                                      icon={ isPasswordVisible ? FiEyeOff : FiEye }
                                      onClick={ () => togglePasswordVisibility( field.key ) }
                                      style={ { cursor: 'pointer' } }
                                    />
                                  ) : undefined }
                                />
                              ) }
                            />
                          </div>
                        );
                      } )}
                    </div>
                  ) : (
                    <Text as="p" size="sm" className={ clsx( styles.credentials_placeholder ) }>
                      No credential fields required for this provider.
                    </Text>
                  )
                ) : (
                  <Text as="p" size="sm" className={ clsx( styles.credentials_placeholder ) }>
                    Credential fields will appear here once a Provider Model is selected.
                  </Text>
                )}
              </div>

              {/* Error Message */}
              {error && (
                <div className={ clsx( styles.error ) }>
                  <Text as="span" size="sm" className={ clsx( styles.error_message ) }>
                    {error}
                  </Text>
                </div>
              )}
            </div>

            {/* Sticky Footer */}
            <div className={ clsx( styles.modal_footer ) }>
              <div className={ clsx( styles.actions ) }>
                {!isFormValid && !isLoading && (
                  <Text as="p" size="sm" className={ clsx( styles.form_note ) }>
                    {mode === 'edit' && !hasFormChanged()
                      ? 'Make changes to update this model'
                      : `Complete all required fields to ${ mode === 'edit' ? 'save changes' : 'add this model' }`}
                  </Text>
                )}
                <div className={ clsx( styles.buttons ) }>
                  <Button
                    variant="secondary"
                    size="md"
                    onClick={ handleCancel }
                    disabled={ isSubmitting }
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="md"
                    type="submit"
                    disabled={ !isFormValid || isSubmitting || isLoading }
                  >
                    {isSubmitting
                      ? ( mode === 'edit' ? 'Saving...' : 'Adding...' )
                      : ( mode === 'edit' ? 'Save Changes' : 'Add Model' )}
                  </Button>
                </div>
              </div>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
}
